/**
 * /intake — live stream of intake-classifier triage decisions.
 *
 * Top: hero stats (24h) — total, orders, spam, avg latency, cost.
 * Middle: channel chips + filter (all / orders / spam).
 * Bottom: table of last 100 classifications, newest-first, auto-refresh
 * every 5s.
 *
 * Designed so an operator can answer "почему мы пропустили эту заявку?"
 * by clicking a row — the drawer shows raw text, model verdict,
 * extracted fields, link to the resulting order (if any).
 */

import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Inbox, Megaphone, X } from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";

import {
  intakeApi,
  type ClassificationDto,
  type IntakeStatsDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { EmptyBlock, ErrorBlock } from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";


type Filter = "all" | "orders" | "spam";


export function IntakePage(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<Filter>("all");
  const [channel, setChannel] = useState<string | null>(null);
  const [active, setActive] = useState<ClassificationDto | null>(null);

  // Stats — refreshed every 10s
  const { data: stats, refetch: refetchStats } = useFetch<IntakeStatsDto>(
    (signal) => intakeApi.stats(24, { signal }),
    [],
  );

  // Stream — refreshed every 5s with current filter
  const fetcher = useMemo(
    () => (signal: AbortSignal) => {
      const params: { limit: number; onlyOrders: boolean; channel?: string } = {
        limit: 100,
        onlyOrders: filter === "orders",
      };
      if (channel) params.channel = channel;
      return intakeApi.list(params, { signal });
    },
    [filter, channel],
  );
  const { data, error, loading, refetch } = useFetch<ClassificationDto[]>(
    fetcher,
    [filter, channel],
  );

  // Light polling — 5s for stream, 10s for stats
  useEffect(() => {
    const a = setInterval(refetch, 5_000);
    const b = setInterval(refetchStats, 10_000);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, [refetch, refetchStats]);

  const rows = (data ?? []).filter((r) =>
    filter === "spam" ? !r.is_order : true,
  );

  return (
    <div className="space-y-4">
      <StatsRibbon stats={stats} />

      <Card>
        <CardHeader
          title={t("intake.title") || "Воронка intake"}
          description={
            t("intake.subtitle") ||
            "Поток сообщений из мессенджеров → классификатор → заявки"
          }
          actions={
            <Button variant="ghost" size="xs" onClick={refetch}>
              {loading ? "…" : (t("common.refresh") || "Обновить")}
            </Button>
          }
        />

        {/* Filter chips */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <FilterChip
            active={filter === "all"}
            label={`Все · ${stats?.total ?? 0}`}
            onClick={() => setFilter("all")}
          />
          <FilterChip
            active={filter === "orders"}
            label={`Заявки · ${stats?.orders ?? 0}`}
            onClick={() => setFilter("orders")}
            tone="ai"
          />
          <FilterChip
            active={filter === "spam"}
            label={`Мусор · ${stats?.spam ?? 0}`}
            onClick={() => setFilter("spam")}
            tone="warning"
          />
          <div className="ml-2 flex flex-wrap gap-1">
            {Object.entries(stats?.by_channel ?? {}).map(([ch, n]) => (
              <button
                key={ch}
                onClick={() => setChannel(channel === ch ? null : ch)}
                className={
                  "rounded-full border px-2 py-0.5 text-2xs font-mono " +
                  (channel === ch
                    ? "border-ai-300 bg-ai-50 text-ai-800"
                    : "border-base-200 bg-surface text-base-600 hover:border-ai-200")
                }
              >
                {ch} · {n}
              </button>
            ))}
            {channel && (
              <button
                onClick={() => setChannel(null)}
                className="rounded-full border border-base-200 bg-surface px-2 py-0.5 text-2xs text-base-500 hover:bg-base-100"
                title="Сбросить канал"
              >
                <X className="size-3 inline" />
              </button>
            )}
          </div>
        </div>

        {error && <ErrorBlock error={error} onRetry={refetch} />}
        {loading && !data && <SkeletonTable rows={6} cols={5} />}
        {data && rows.length === 0 && (
          <EmptyBlock
            title={t("intake.empty.title") || "Пока нет классификаций"}
            description={
              t("intake.empty.description") ||
              "Подключите Telegram-бот или WhatsApp — сообщения появятся здесь по мере поступления."
            }
          />
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base-200 text-2xs uppercase tracking-wide text-base-500">
                  <th className="py-2 pr-2 text-left font-medium">Время</th>
                  <th className="py-2 pr-2 text-left font-medium">Канал</th>
                  <th className="py-2 pr-2 text-left font-medium">Сообщение</th>
                  <th className="py-2 pr-2 text-right font-medium">Уверенность</th>
                  <th className="py-2 pr-2 text-left font-medium">Итог</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <ClassificationRow
                    key={r.id}
                    row={r}
                    onOpen={() => setActive(r)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {active && (
        <ClassificationDrawer
          row={active}
          onClose={() => setActive(null)}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Hero stats ribbon
// ---------------------------------------------------------------------------

function StatsRibbon({ stats }: { stats: IntakeStatsDto | null }): JSX.Element {
  const t = useT();
  const orders = stats?.orders ?? 0;
  const total = stats?.total ?? 0;
  const orderRate = total > 0 ? Math.round((orders / total) * 100) : 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
      <StatCard
        label={t("intake.stat.total") ?? "Сообщений / 24ч"}
        value={String(stats?.total ?? 0)}
        icon={<Megaphone className="size-4 text-base-400" />}
      />
      <StatCard
        label={t("intake.stat.orders") ?? "Стало заявками"}
        value={String(orders)}
        hint={`${orderRate}%`}
        tone="ai"
        icon={<Inbox className="size-4 text-ai-600" />}
      />
      <StatCard
        label={t("intake.stat.spam") ?? "Отфильтровано"}
        value={String(stats?.spam ?? 0)}
        hint={t("intake.stat.spam.hint") ?? "спам / мета / болтовня"}
      />
      <StatCard
        label={t("intake.stat.latency") ?? "Среднее время"}
        value={`${stats?.avg_latency_ms ?? 0} мс`}
        hint={t("intake.stat.latency.hint") ?? "qwen + gpt"}
      />
      <StatCard
        label={t("intake.stat.cost") ?? "Стоимость LLM"}
        value={`$${(stats?.total_cost_usd ?? 0).toFixed(3)}`}
        hint={t("intake.stat.cost.hint") ?? "за 24 часа"}
      />
    </div>
  );
}

function StatCard({
  label, value, hint, tone, icon,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "ai";
  icon?: JSX.Element;
}): JSX.Element {
  return (
    <div className={
      "rounded-md border p-3 " +
      (tone === "ai"
        ? "border-ai-200 bg-ai-50/40"
        : "border-base-200 bg-surface")
    }>
      <div className="flex items-center justify-between text-2xs uppercase tracking-wide text-base-500">
        <span>{label}</span>
        {icon}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums">
        {value}
      </div>
      {hint && (
        <div className={"mt-0.5 text-xs " + (tone === "ai" ? "text-ai-700" : "text-base-500")}>
          {hint}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Filter chip
// ---------------------------------------------------------------------------

function FilterChip({
  active, label, onClick, tone,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  tone?: "ai" | "warning";
}): JSX.Element {
  const colors = active
    ? tone === "ai"
      ? "border-ai-400 bg-ai-100 text-ai-800"
      : tone === "warning"
      ? "border-heritage-300 bg-heritage-50 text-heritage-800"
      : "border-base-300 bg-base-100 text-base-900"
    : "border-base-200 bg-surface text-base-600 hover:border-base-300";
  return (
    <button
      onClick={onClick}
      className={"rounded-md border px-3 py-1 text-sm font-medium transition-colors " + colors}
    >
      {label}
    </button>
  );
}


// ---------------------------------------------------------------------------
// Row + drawer
// ---------------------------------------------------------------------------

function ClassificationRow({
  row, onOpen,
}: { row: ClassificationDto; onOpen: () => void }): JSX.Element {
  const time = new Date(row.created_at);
  const timeStr = `${time.getHours().toString().padStart(2, "0")}:${time
    .getMinutes()
    .toString()
    .padStart(2, "0")}:${time.getSeconds().toString().padStart(2, "0")}`;

  const channelTone =
    row.channel.startsWith("telegram") ? "ai"
    : row.channel === "whatsapp"       ? "success"
    : "neutral";

  const conf = Math.round(row.confidence * 100);
  return (
    <tr
      onClick={onOpen}
      className="group cursor-pointer border-b border-base-100 hover:bg-ai-50/30"
    >
      <td className="py-1.5 pr-2 font-mono text-xs tabular-nums text-base-500">{timeStr}</td>
      <td className="py-1.5 pr-2">
        <Badge tone={channelTone as any}>{row.channel}</Badge>
      </td>
      <td className="py-1.5 pr-2 max-w-md truncate text-base-800">
        {row.raw_text || "(пусто)"}
      </td>
      <td className="py-1.5 pr-2 text-right">
        <ConfidenceBar value={conf} positive={row.is_order} />
      </td>
      <td className="py-1.5 pr-2">
        {row.is_order ? (
          <span className="inline-flex items-center gap-1 text-ai-700">
            <Inbox className="size-3.5" />
            <span className="text-xs font-medium">
              {row.extracted_fields?.eq_type as string | undefined ?? "заявка"}
            </span>
            {row.order_id && (
              <ArrowRight className="size-3 text-ai-500" />
            )}
          </span>
        ) : (
          <span className="text-xs text-base-500">
            {DROP_REASON_LABEL[row.drop_reason ?? "—"] ?? row.drop_reason ?? "—"}
          </span>
        )}
      </td>
    </tr>
  );
}

const DROP_REASON_LABEL: Record<string, string> = {
  not_order:         "не заявка",
  low_confidence:    "ниже порога",
  extraction_failed: "не извлекли поля",
  empty:             "пустое",
  classifier_error:  "ошибка LLM",
  spam:              "спам",
};


function ConfidenceBar({ value, positive }: { value: number; positive: boolean }): JSX.Element {
  const color = positive ? "bg-ai-500" : value > 30 ? "bg-heritage-400" : "bg-base-300";
  return (
    <div className="inline-flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-base-100">
        <div className={"h-full " + color} style={{ width: `${value}%` }} />
      </div>
      <span className="font-mono text-xs tabular-nums text-base-700">{value}%</span>
    </div>
  );
}


function ClassificationDrawer({
  row, onClose,
}: { row: ClassificationDto; onClose: () => void }): JSX.Element {
  const ex = row.extracted_fields ?? {};
  return (
    <Card>
      <CardHeader
        title={`Классификация · ${row.is_order ? "заявка" : "отброшено"}`}
        description={`${row.channel} · ${row.channel_thread_id ?? "—"}`}
        actions={
          <Button variant="ghost" size="xs" onClick={onClose}>
            <X className="size-3.5" />
          </Button>
        }
      />
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <div className="text-2xs uppercase tracking-wide text-base-500">Текст</div>
          <div className="mt-1 whitespace-pre-wrap break-words rounded-md border border-base-200 bg-base-50 p-3 text-sm">
            {row.raw_text || "(пусто)"}
          </div>
        </div>
        <div className="text-sm">
          <div className="text-2xs uppercase tracking-wide text-base-500">Решение</div>
          <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1">
            <dt className="text-base-500">Уверенность</dt>
            <dd className="font-mono tabular-nums">{Math.round(row.confidence * 100)}%</dd>
            <dt className="text-base-500">First-pass</dt>
            <dd className="font-mono">{row.model_first_pass ?? "—"}</dd>
            {row.model_extract && (
              <>
                <dt className="text-base-500">Extract</dt>
                <dd className="font-mono">{row.model_extract}</dd>
              </>
            )}
            <dt className="text-base-500">Latency</dt>
            <dd className="font-mono tabular-nums">{row.latency_ms} мс</dd>
            <dt className="text-base-500">Cost</dt>
            <dd className="font-mono tabular-nums">${row.cost_usd.toFixed(5)}</dd>
            {row.drop_reason && (
              <>
                <dt className="text-base-500">Причина дропа</dt>
                <dd>{DROP_REASON_LABEL[row.drop_reason] ?? row.drop_reason}</dd>
              </>
            )}
          </dl>
          {row.is_order && Object.keys(ex).length > 0 && (
            <>
              <div className="mt-3 text-2xs uppercase tracking-wide text-base-500">Извлечено</div>
              <pre className="mt-1 overflow-x-auto rounded-md border border-base-200 bg-base-50 p-3 font-mono text-2xs">
                {JSON.stringify(ex, null, 2)}
              </pre>
            </>
          )}
          {row.order_id && (
            <div className="mt-3 text-xs">
              → создана заявка{" "}
              <code className="rounded bg-ai-50 px-1.5 py-0.5 font-mono text-ai-800">
                {row.order_id.slice(0, 8)}…
              </code>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
